import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getStripeKey, getStripeKeyForUser } from '../utils/stripe-config';

// Enhanced Stripe loading with fallbacks and retries
// Cache instances by key to avoid reloading when switching between test/live
const stripeInstances: Record<string, Stripe> = {};
let stripePromise: Promise<Stripe | null> | null = null;

const loadStripeWithFallback = async (stripeKey?: string, retries = 3, delay = 1000): Promise<Stripe | null> => {
  const key = stripeKey || getStripeKey();

  // Return cached instance if available
  if (stripeInstances[key]) {
    return stripeInstances[key];
  }

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const isTest = key.startsWith('pk_test_');
      console.log('Stripe loading attempt ' + attempt + '/' + retries + ' (' + (isTest ? 'TEST' : 'LIVE') + ')');

      // Try to load Stripe with timeout
      const stripe = await Promise.race([
        loadStripe(key),
        new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('Stripe loading timeout')), 10000)
        )
      ]);

      if (stripe) {
        console.log('Stripe loaded successfully (' + (isTest ? 'TEST' : 'LIVE') + ' mode)');
        stripeInstances[key] = stripe;
        return stripe;
      }
    } catch (error) {
      console.log('Stripe loading attempt ' + attempt + ' failed:', error);

      if (attempt < retries) {
        console.log('Retrying in ' + delay + 'ms...');
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 1.5; // Exponential backoff
      }
    }
  }

  console.error('All Stripe loading attempts failed');
  return null;
};

// Initialize Stripe with fallback strategy (default environment-based key)
const getStripePromise = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    stripePromise = loadStripeWithFallback();
  }
  return stripePromise;
};

// Get Stripe instance for a specific user (test users get sandbox)
const getStripeForUser = (email: string | null | undefined): Promise<Stripe | null> => {
  const key = getStripeKeyForUser(email);
  return loadStripeWithFallback(key);
};

export interface PaymentRequest {
  email: string;
  tier: 'supporter';
  memo?: string;
  country?: string;
  postalCode?: string;
  promoCode?: string;
  amount?: number;
}

export interface PaymentResponse {
  success: boolean;
  clientSecret?: string;
  error?: string;
}

class PaymentService {
  private baseUrl: string = (() => {
    const hostname = window.location.hostname;
    if (hostname === 'larryshelp.org' || hostname === 'www.larryshelp.org') {
      return 'https://api.larryshelp.org';
    }
    return 'http://localhost:8000';
  })();

  async createPaymentIntent(paymentData: PaymentRequest): Promise<PaymentResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/payments/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Payment intent creation failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Payment failed'
      };
    }
  }

  async confirmPayment(clientSecret: string, paymentMethodId: string, userEmail?: string): Promise<PaymentResponse> {
    try {
      // Use user-specific Stripe instance if email provided
      const stripe = userEmail ? await getStripeForUser(userEmail) : await getStripePromise();
      if (!stripe) {
        throw new Error('Stripe failed to load after multiple attempts');
      }

      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodId
      });

      if (error) {
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true
      };
    } catch (error) {
      console.error('Payment confirmation failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Payment confirmation failed'
      };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createPaymentMethod(cardElement: any, userEmail?: string): Promise<string | null> {
    try {
      // Use user-specific Stripe instance if email provided
      const stripe = userEmail ? await getStripeForUser(userEmail) : await getStripePromise();
      if (!stripe) {
        throw new Error('Stripe failed to load after multiple attempts');
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement
      });

      if (error) {
        throw new Error(error.message);
      }

      return paymentMethod.id;
    } catch (error) {
      console.error('Payment method creation failed:', error);
      return null;
    }
  }

  // Check if Stripe is available
  isStripeAvailable(): boolean {
    return Object.keys(stripeInstances).length > 0;
  }

  // Force reload Stripe (useful for recovery)
  async reloadStripe(): Promise<boolean> {
    console.log('Forcing Stripe reload...');
    stripePromise = null;
    // Clear all cached instances
    Object.keys(stripeInstances).forEach(key => delete stripeInstances[key]);

    try {
      const stripe = await loadStripeWithFallback();
      return stripe !== null;
    } catch (error) {
      console.error('Stripe reload failed:', error);
      return false;
    }
  }
}

export const paymentService = new PaymentService();
export default paymentService;
